import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { Tilt } from "@/components/Tilt";
import { ParticleSphere } from "@/components/ParticleSphere";
import { SEO } from "@/components/SEO";
import { COMPANIES } from "@/data/content";

const SECTORS = [
    {
        key: "seeds",
        index: "01",
        name: "Agriculture & Hybrid Seeds",
        note: "The foundation of MS Group — four seed companies backed by a network of 5,000+ dealers and the trust of over a million farmer families across India.",
        ids: ["my-seeds", "navayuga-seeds", "seven-seeds", "seed-park"],
    },
    {
        key: "fmcg",
        index: "02",
        name: "FMCG",
        note: "Everyday food essentials for Indian households — made with the discipline of the farm.",
        ids: ["mega-foods"],
    },
    {
        key: "realty",
        index: "03",
        name: "Real Estate",
        note: "Land and spaces developed with transparency, planned for appreciation.",
        ids: ["navayuga-ventures"],
    },
    {
        key: "health",
        index: "04",
        name: "Pharma & Healthcare",
        note: "Clinician-led pharmaceutical and healthcare specialities, built on compassion.",
        ids: ["navayuga-healthcare"],
    },
];

const CompanyCard = ({ company }) => (
    <Tilt className="h-full" max={4}>
        <article
            data-testid={`company-card-${company.id}`}
            className="group flex h-full flex-col border border-[#E8E6DF] bg-white transition-all duration-500 hover:shadow-[0_36px_70px_-36px_rgba(10,13,32,0.4)]"
        >
        <div className="relative flex h-48 items-center justify-center overflow-hidden border-b border-[#EFEDE6] bg-white px-10">
            <span
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{ background: `radial-gradient(ellipse 65% 80% at 50% 60%, ${company.color}14 0%, transparent 70%)` }}
            />
            {company.logo ? (
                <img
                    src={company.logo}
                    alt={`${company.name} logo`}
                    loading="lazy"
                    className="relative max-h-24 w-auto object-contain transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-[1.06]"
                />
            ) : (
                <span className="font-display relative text-3xl font-semibold tracking-wide" style={{ color: company.color }}>
                    7 <span className="font-light">SEEDS</span>
                </span>
            )}
        </div>
        <div className="h-1 w-full" style={{ backgroundColor: company.color }} />
        <div className="flex flex-1 flex-col p-8">
            <span
                className="inline-flex w-fit items-center px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: company.color, backgroundColor: `${company.color}14` }}
            >
                {company.sector}
            </span>
            <h3 className="font-display mt-4 text-2xl font-medium tracking-tight text-[#0F172A]">{company.name}</h3>
            <p className="mt-1 text-sm italic text-slate-500">{company.tagline}</p>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{company.desc}</p>
            <div className="mt-auto pt-8">
                {company.url ? (
                    <a
                        href={company.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`company-visit-${company.id}`}
                        className="link-sweep inline-flex items-center gap-2 text-sm font-semibold"
                        style={{ color: company.color }}
                    >
                        Visit Website
                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </a>
                ) : (
                    <span className="text-[13px] uppercase tracking-[0.18em] text-slate-400">An MS Group Company</span>
                )}
            </div>
        </div>
        </article>
    </Tilt>
);

export default function GroupPage() {
    return (
        <main data-testid="group-page">
            <SEO
                title="Our Group | Seven Companies Across Four Industries — MS Group"
                description="Explore the MS Group companies: My Seeds, Navayuga Seeds, Seven Seeds and Seed Park in hybrid seeds; Mega Foods in FMCG; Navayuga Ventures in real estate; Navayuga Healthcare in pharma."
            />
            <section className="grain relative flex min-h-[100svh] flex-col justify-center overflow-hidden bg-[#0A0D20] pb-16 pt-36 md:pb-20 md:pt-44" data-testid="group-hero">
                <div className="pointer-events-none absolute -right-28 top-1/2 hidden -translate-y-1/2 lg:block">
                    <ParticleSphere className="h-[440px] w-[440px] opacity-60" />
                </div>
                <div className="pointer-events-none absolute -right-8 bottom-0 select-none font-display text-[13rem] font-semibold leading-none text-outline opacity-40">
                    7
                </div>
                <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <Reveal>
                        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#F16622]">
                            <span className="inline-block h-px w-10 bg-[#F16622]" />
                            Our Group
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="font-display mt-6 max-w-4xl text-[clamp(2.4rem,6svh,4.5rem)] font-medium leading-[1.06] tracking-tight text-white">
                            One Group. Seven Businesses. Four Industries.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
                            Each MS Group company carries its own identity, leadership and ambition — united by one
                            promise of trust, quality and generational thinking.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* quick-jump sector anchors */}
            <div className="sticky top-24 z-40 border-b border-[#E4E2DA] bg-white/90 backdrop-blur-md md:top-28" data-testid="sector-anchors">
                <div className="mx-auto flex max-w-[1440px] items-center gap-2 overflow-x-auto px-6 py-3 md:px-12 lg:px-20">
                    {SECTORS.map((s) => (
                        <button
                            key={s.key}
                            data-testid={`anchor-${s.key}`}
                            onClick={() => document.getElementById(`sector-${s.key}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            className="group flex shrink-0 items-center gap-2.5 border border-[#E4E2DA] px-4 py-2 transition-colors duration-300 hover:border-[#F16622] hover:bg-[#F16622]"
                        >
                            <span className="font-display text-xs font-medium text-[#F16622] transition-colors group-hover:text-white">
                                {s.index}
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0F172A] transition-colors group-hover:text-white">
                                {s.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {SECTORS.map((sector, si) => (
                <section
                    key={sector.key}
                    id={`sector-${sector.key}`}
                    className={`scroll-mt-36 py-14 md:scroll-mt-44 md:py-20 ${si % 2 === 0 ? "bg-white" : "bg-[#F8F7F3]"}`}
                    data-testid={`sector-${sector.key}`}
                >
                    <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                        <div
                            className={`flex flex-wrap items-end justify-between gap-8 border-b border-[#E4E2DA] pb-10 ${
                                sector.key === "realty" ? "md:flex-row-reverse" : ""
                            }`}
                        >
                            <div className={`flex items-start gap-6 md:gap-10 ${sector.key === "realty" ? "md:flex-row-reverse md:text-right" : ""}`}>
                                <span className="font-display text-5xl font-light text-[#F16622] md:text-7xl">{sector.index}</span>
                                <div>
                                    <Reveal>
                                        <h2 className="font-display text-3xl font-medium tracking-tight text-[#0F172A] md:text-5xl">
                                            {sector.name}
                                        </h2>
                                    </Reveal>
                                    <Reveal delay={0.1}>
                                        <p className={`mt-4 max-w-xl text-base leading-relaxed text-slate-600 ${sector.key === "realty" ? "md:ml-auto" : ""}`}>
                                            {sector.note}
                                        </p>
                                    </Reveal>
                                </div>
                            </div>
                            <span className="text-xs uppercase tracking-[0.25em] text-slate-400" data-testid={`sector-est-${sector.key}`}>
                                {sector.ids.length === 1
                                    ? (() => {
                                          const c = COMPANIES.find((x) => x.id === sector.ids[0]);
                                          return c.id === "navayuga-healthcare" ? `Opening ${c.est}` : `Est. ${c.est}`;
                                      })()
                                    : `${sector.ids.length} Companies`}
                            </span>
                        </div>
                        <div
                            className={`mt-12 grid gap-6 ${
                                sector.ids.length > 1 ? "sm:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-2"
                            }`}
                        >
                            {sector.ids.map((id, i) => {
                                const company = COMPANIES.find((c) => c.id === id);
                                return (
                                    <Reveal key={id} delay={i * 0.07} className={sector.key === "realty" && sector.ids.length === 1 ? "md:col-start-2" : ""}>
                                        <CompanyCard company={company} />
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </section>
            ))}

            <section className="grain relative bg-[#0A0D20] py-24 md:py-28" data-testid="group-cta">
                <div className="relative mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-10 px-6 md:px-12 lg:px-20">
                    <SectionHead
                        dark
                        overline="Work With Us"
                        title="Interested in partnering with an MS Group company?"
                    />
                    <Reveal delay={0.15}>
                        <Link
                            to="/contact"
                            data-testid="group-contact-btn"
                            className="group inline-flex items-center gap-3 bg-[#F16622] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-[#0A0D20]"
                        >
                            Start a conversation
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
